/**
 * Operator: control-plane audit trail (plan §16.3, §26).
 *
 * Every consequential action leaves a row here. Metadata is already passed through the log
 * redactor at write time, so nothing on this screen can contain a provider secret — but it is
 * still operator-only, because actor ids and account ids are not public information.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client.js";
import { Empty, Loading, formatDate } from "../../components/ui.js";
import { AdminError, AdminNav } from "./shared.js";
import type { AuditEvent } from "../../api/types.js";

const ACTION_GROUPS = [
  { label: "All", value: "" },
  { label: "Billing", value: "billing." },
  { label: "Deployments", value: "deployment." },
  { label: "Provisioning", value: "provisioning." },
  { label: "Credentials", value: "credential." },
  { label: "Pairing", value: "pairing." },
  { label: "Exports", value: "export." },
  { label: "Accounts", value: "account." },
];

export default function AdminAudit() {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [prefix, setPrefix] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminAudit({ limit: "100" });
      setEvents(res.events as AuditEvent[]);
      setError(null);
    } catch (e) {
      setError(e);
      setEvents(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = (events ?? []).filter((e) => !prefix || e.action.startsWith(prefix));

  return (
    <>
      <div className="page-head">
        <p className="eyebrow">Operator</p>
        <h1>Audit</h1>
      </div>
      <AdminNav />

      <div className="row" style={{ marginBottom: 20 }}>
        <label className="field" style={{ marginTop: 0, minWidth: 240 }}>
          <span className="field__label">Filter</span>
          <select className="select" value={prefix} onChange={(e) => setPrefix(e.target.value)}>
            {ACTION_GROUPS.map((g) => (
              <option key={g.value || "all"} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? <AdminError error={error} onRetry={() => void load()} /> : null}
      {loading ? <Loading label="Loading audit events" /> : null}

      {!loading && events && visible.length === 0 ? (
        <Empty title="No events">
          <p>Nothing recorded for this filter.</p>
        </Empty>
      ) : null}

      {!loading && visible.length > 0 ? (
        <div className="card table-scroll" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Actor</th>
                <th scope="col">Action</th>
                <th scope="col">Account</th>
                <th scope="col">Metadata</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => (
                <tr key={e.id}>
                  <td className="mono small">{formatDate(e.created_at)}</td>
                  <td className="muted small">{e.actor}</td>
                  <td className="mono small">{e.action}</td>
                  <td className="mono small muted">{e.account_id?.slice(0, 8) ?? "—"}</td>
                  <td className="mono small muted" style={{ maxWidth: 420, wordBreak: "break-all" }}>
                    {JSON.stringify(e.metadata)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
